import type { AppRole } from '../contexts/auth-context';

export const PERMISSIONS = [
  'x5.read',
  'x5.write',
  'x5.org.create',
  'x5.org.write',
  'x5.org.members',
  'datalake.read',
  'datalake.execute',
  'reports.read',
  'reporting.read',
  'reporting.preview',
  'reporting.run',
  'reporting.write',
  'exporter.read',
  'exporter.run',
  'exporter.delete',
  'appstream.read',
  'appstream.terminate',
  'sms.read',
  'sms.write',
  'sms.acquire',
  'cellular.read',
  'cellular.diagnose',
  'cellular.notify',
  'gateway.read',
  'gateway.query',
  'gateway.wan',
  'gateway.reboot',
  'admin.users.read',
  'admin.users.write',
  'admin.roles.read',
  'audit.read',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export function isPermission(value: unknown): value is Permission {
  return typeof value === 'string' && (PERMISSIONS as readonly string[]).includes(value);
}

export type PermissionGroup =
  | 'X5 Setup'
  | 'Datalake'
  | 'Reports Monitor'
  | 'Reporting'
  | 'Exporter'
  | 'WorkSpaces Apps'
  | 'SMS Numbers'
  | 'Cellular'
  | 'Gateways'
  | 'Administration';

export interface PermissionDef {
  key: Permission;
  label: string;
  description: string;
  group: PermissionGroup;
  /**
   * The routes that actually check this permission. The Roles page publishes
   * these, so a permission that gates nothing is visibly a claim the UI would
   * be making falsely — `reserved` is how an unenforced entry has to declare
   * itself instead.
   */
  enforcedBy: string[];
  reserved?: boolean;
}

export const PERMISSION_CATALOG: PermissionDef[] = [
  {
    key: 'x5.read',
    label: 'View X5 Setup',
    description: 'Browse Organizations, Users, and Applications.',
    group: 'X5 Setup',
    enforcedBy: [
      'GET /organizations',
      'GET /organizations/{id}',
      'GET /users',
      'GET /users/identities',
      'GET /users/{username}/identity',
      'GET /applications',
    ],
  },
  {
    key: 'x5.write',
    label: 'Create X5 users',
    description: 'Create new X5 customer accounts in the customer Cognito pool.',
    group: 'X5 Setup',
    enforcedBy: ['POST /users'],
  },
  {
    key: 'x5.org.create',
    label: 'Create organizations',
    description:
      'Add a new organization. There is no delete path, so a new organization is permanent — and the table is capped at 500.',
    group: 'X5 Setup',
    enforcedBy: ['POST /organizations'],
  },
  {
    key: 'x5.org.write',
    label: 'Edit organization details',
    description: "Rename an organization and edit its company and description.",
    group: 'X5 Setup',
    enforcedBy: ['PATCH /organizations/{id}'],
  },
  {
    key: 'x5.org.members',
    label: 'Change organization membership',
    description:
      "Attach and detach users and applications. This moves the data a customer can see, so it is separate from editing an organization's name.",
    group: 'X5 Setup',
    enforcedBy: ['POST /organizations/{id}/members', 'PATCH /users/{id}'],
  },
  {
    key: 'datalake.read',
    label: 'View Datalake monitoring',
    description: 'See datalake pipeline runs, throughput, and per-server breakdowns.',
    group: 'Datalake',
    enforcedBy: [
      'GET /datalake/runs',
      'GET /datalake/runs/{runId}',
      'GET /datalake/executions',
      'GET /datalake/executions/{executionArn}',
      'GET /datalake/clients',
      'GET /datalake/coverage',
      'GET /datalake/customers',
      'GET /datalake/metrics',
    ],
  },
  {
    key: 'datalake.execute',
    label: 'Trigger datalake actions',
    description: 'Kick off backfills or other datalake operations. (Reserved.)',
    group: 'Datalake',
    enforcedBy: [],
    reserved: true,
  },
  {
    key: 'reports.read',
    label: 'View Reports monitoring',
    description: 'See report run history, summaries, and error details.',
    group: 'Reports Monitor',
    enforcedBy: [
      'GET /reports/runs',
      'GET /reports/runs/{reportId}/{runTimestamp}',
      'GET /reports/summary',
    ],
  },
  {
    key: 'reporting.read',
    label: 'View report configurations',
    description: 'See defined reports, schedules, recipients, and templates.',
    group: 'Reporting',
    enforcedBy: ['GET /reports/configs'],
  },
  {
    key: 'reporting.preview',
    label: 'Preview a report',
    description: 'Run the Athena query and render the email body without sending.',
    group: 'Reporting',
    enforcedBy: ['POST /reports/preview'],
  },
  {
    key: 'reporting.run',
    label: 'Manually run a report (sends email)',
    description: 'Trigger a real, email-sending report run on demand.',
    group: 'Reporting',
    enforcedBy: ['POST /reports/run'],
  },
  {
    key: 'reporting.write',
    label: 'Edit reports',
    description: 'Create, edit, enable, or disable reports. (Reserved.)',
    group: 'Reporting',
    enforcedBy: [],
    reserved: true,
  },
  {
    key: 'exporter.read',
    label: 'View Data Exporter',
    description:
      'Browse completed client data exports, the links issued from them, and download activity. Download URLs themselves stay hidden.',
    group: 'Exporter',
    enforcedBy: [
      'GET /exporter/exports',
      'GET /exporter/links',
      'GET /exporter/access-logs',
    ],
  },
  {
    key: 'exporter.run',
    label: 'Run Data Exporter',
    description:
      'Trigger a client data export, generate / email download links, revoke an issued link, and read back the live URLs of links that are still valid.',
    group: 'Exporter',
    enforcedBy: [
      'POST /exporter/exports',
      'POST /exporter/links',
      'POST /exporter/links/revoke',
      'GET /exporter/links',
    ],
  },
  {
    key: 'exporter.delete',
    label: 'Delete completed exports',
    description:
      'Permanently remove an export’s archives from S3. Any download link still outstanding for it stops working.',
    group: 'Exporter',
    enforcedBy: ['DELETE /exporter/exports'],
  },
  {
    key: 'appstream.read',
    label: 'View WorkSpaces Applications',
    description:
      'See live streaming sessions and who owns them, fleet capacity, and historical usage by user and organization.',
    group: 'WorkSpaces Apps',
    enforcedBy: ['GET /appstream/sessions', 'GET /appstream/usage'],
  },
  {
    key: 'appstream.terminate',
    label: 'Terminate a streaming session',
    description:
      'Immediately stop a live session. The customer is dropped out of their application with no warning, and unsaved work in it is lost.',
    group: 'WorkSpaces Apps',
    enforcedBy: ['DELETE /appstream/sessions'],
  },
  {
    key: 'sms.read',
    label: 'View SMS Numbers',
    description:
      'See every leased 10DLC number, which X5 server and customer it is assigned to, which are spare, and where the registry and AWS disagree.',
    group: 'SMS Numbers',
    enforcedBy: ['GET /sms/numbers'],
  },
  {
    key: 'sms.write',
    label: 'Assign SMS numbers',
    description:
      'Assign, move, and remove the 10DLC number an X5 server sends from, and enable or disable two-way messaging on a number. Writes to the table the SMS gateway reads on every send, so a wrong entry changes or breaks a customer’s alarm sender.',
    group: 'SMS Numbers',
    enforcedBy: ['POST /sms/assignments', 'DELETE /sms/assignments', 'PATCH /sms/numbers'],
  },
  {
    key: 'sms.acquire',
    label: 'Acquire new SMS numbers',
    description:
      'Lease an additional 10DLC number from AWS. Commits recurring monthly cost and consumes a finite account quota, and releasing a number afterwards does not give it back.',
    group: 'SMS Numbers',
    enforcedBy: ['POST /sms/numbers'],
  },
  {
    key: 'cellular.read',
    label: 'View Cellular data',
    description:
      'See every Hologram SIM, the Teltonika router it sits in, which customer it serves, what it used and costs, and which sites are running on cellular right now.',
    group: 'Cellular',
    enforcedBy: [
      'GET /cellular/sims',
      'POST /snapshot/refresh',
      'GET /snapshot/refresh/{runId}',
      'GET /cellular/notices',
      'GET /cellular/notices/{customerKey}',
    ],
  },
  {
    key: 'cellular.diagnose',
    label: 'Check a router’s WAN',
    description:
      'Run the fixed, read-only WAN check on a customer’s Teltonika router through RMS to see why a site is on 4G. It executes on customer hardware, so every check is audited before it runs.',
    group: 'Cellular',
    enforcedBy: ['POST /cellular/sims/{deviceId}/wan-check', 'GET /cellular/wan-check'],
  },
  {
    key: 'cellular.notify',
    label: 'Notify customers about 4G use',
    description:
      'Decide which customers running on their cellular backup are emailed, and to whom, and send a test notice to yourself. Choosing “notify” lets the nightly run email that customer.',
    group: 'Cellular',
    enforcedBy: ['POST /cellular/notices/{customerKey}/decision', 'POST /cellular/notices/{customerKey}/test'],
  },
  {
    key: 'gateway.read',
    label: 'View Gateways',
    description:
      'See every Teltonika gateway, the link it is on, whether it is wired to a SIM, server and customer correctly, and the history of commands sent to it and what it replied.',
    group: 'Gateways',
    enforcedBy: [
      'GET /gateways',
      'GET /gateways/commands',
      'GET /gateways/{routerId}/commands',
      'POST /snapshot/refresh',
      'GET /snapshot/refresh/{runId}',
    ],
  },
  {
    key: 'gateway.query',
    label: 'Send read-only gateway commands',
    description:
      'Send the fixed read-only commands — status, WAN check, WAN config — over RMS, or by SMS when RMS cannot reach the gateway. They execute on customer hardware, so each attempt is audited before it is sent.',
    group: 'Gateways',
    enforcedBy: ['POST /gateways/{routerId}/commands'],
  },
  {
    key: 'gateway.wan',
    label: 'Disable / enable a gateway’s WAN',
    description:
      'Take a gateway’s wired WAN out of service (forcing cellular) or put it back, over RMS or by SMS. Changes the customer’s gateway configuration, so every send needs a reason, and the SMS route needs the gateway admin password.',
    group: 'Gateways',
    enforcedBy: ['POST /gateways/{routerId}/commands'],
  },
  {
    key: 'gateway.reboot',
    label: 'Reboot a gateway',
    description:
      'Restart a customer’s gateway, over RMS or by SMS — the step that applies a WAN change sent by SMS. The site loses its internet and the X5 tunnel for a couple of minutes, so every reboot needs a reason, and the SMS route needs the gateway admin password.',
    group: 'Gateways',
    enforcedBy: ['POST /gateways/{routerId}/commands'],
  },
  {
    key: 'admin.users.read',
    label: 'View Admin Users',
    description: 'See who has access to this portal and at what role.',
    group: 'Administration',
    enforcedBy: ['GET /admin-users', 'GET /admin-users/{email}/cognito'],
  },
  {
    key: 'admin.users.write',
    label: 'Manage Admin Users',
    description: 'Add, remove, change roles, and edit permission overrides.',
    group: 'Administration',
    enforcedBy: [
      'POST /admin-users',
      'PATCH /admin-users/{email}',
      'DELETE /admin-users/{email}',
    ],
  },
  {
    key: 'admin.roles.read',
    label: 'View Roles',
    description: 'See the role definitions and the full permission matrix.',
    group: 'Administration',
    enforcedBy: ['GET /roles'],
  },
  {
    key: 'audit.read',
    label: 'View the Audit Log',
    description: 'Read the audit trail of logins, changes, and data exports.',
    group: 'Administration',
    enforcedBy: ['GET /audit'],
  },
];

const BY_KEY = new Map(PERMISSION_CATALOG.map((d) => [d.key, d]));

export function permissionDef(key: Permission): PermissionDef {
  const def = BY_KEY.get(key);
  if (!def) throw new Error(`No catalog entry for permission ${key}`);
  return def;
}

const ALL_PERMISSIONS: Permission[] = [...PERMISSIONS];

const SAFETYNET_DEFAULTS: Permission[] = [
  'x5.read',
  'datalake.read',
  'reports.read',
  'reporting.read',
  'reporting.preview',
  'appstream.read',
  'sms.read',
  'cellular.read',
  'cellular.notify',
  'gateway.read',
  'gateway.query',
  'gateway.wan',
  'gateway.reboot',
  'admin.users.read',
  'admin.roles.read',
  'audit.read',
];

export const ROLE_DEFAULTS: Record<AppRole, ReadonlySet<Permission>> = {
  Admin: new Set(ALL_PERMISSIONS),
  SafetyNet: new Set(SAFETYNET_DEFAULTS),
};

// Mirrors ROLES in api/src/permissions.ts; api/test/permissions-sync.test.ts
// fails if the two drift.
export const ROLE_DESCRIPTIONS: Record<AppRole, string> = {
  Admin: 'Full access to everything in the portal, including running exports and reports.',
  SafetyNet:
    'Oversight and on-call: can view everything, including admin users, roles, and the audit log, and can send the fixed gateway commands (including the WAN toggle). Cannot otherwise change or export anything.',
};

export const ROLE_ORDER: AppRole[] = ['Admin', 'SafetyNet'];

export function defaultPermissionsFor(role: AppRole): ReadonlySet<Permission> {
  return ROLE_DEFAULTS[role];
}

export function computeEffectivePermissions(
  role: AppRole,
  granted: readonly string[] = [],
  denied: readonly string[] = [],
): Set<Permission> {
  const effective = new Set<Permission>(ROLE_DEFAULTS[role]);
  for (const g of granted) {
    if (isPermission(g)) effective.add(g);
  }
  for (const d of denied) {
    if (isPermission(d)) effective.delete(d);
  }
  return effective;
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  'X5 Setup',
  'Datalake',
  'Reports Monitor',
  'Reporting',
  'Exporter',
  'WorkSpaces Apps',
  'SMS Numbers',
  'Cellular',
  'Gateways',
  'Administration',
];

export function permissionsByGroup(): { group: PermissionGroup; permissions: PermissionDef[] }[] {
  return PERMISSION_GROUPS.map((group) => ({
    group,
    permissions: PERMISSION_CATALOG.filter((d) => d.group === group),
  }));
}
